export { Kpi, Section, TrendChip } from '@/components/common/Blocks'
import { useTranslation } from 'react-i18next'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Select } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { fmtMoney } from '../../shared/format'
import type { EDocStatus, PayStatus, ReviewStatus, VatStatus } from '../types'
import { FINANCE_MONTHS, monthLabel } from '../ui'
import { chartColor } from '../visuals'

export function Money({ value, className, signed }: { value: string; className?: string; signed?: boolean }) {
  const neg = value.startsWith('-')
  return <span className={cn('tabular-nums', signed && (neg ? 'text-destructive' : 'text-success'), className)}>{fmtMoney(value)}</span>
}

export function PeriodSelect({ value, onChange, className }: { value: string; onChange: (m: string) => void; className?: string }) {
  const { t } = useTranslation()
  return (
    <Select
      className={cn('w-44', className)}
      value={value}
      onChange={onChange}
      aria-label={t('finance.period')}
      options={[...FINANCE_MONTHS].reverse().map((m) => ({ value: m, label: monthLabel(m) }))}
    />
  )
}

const PAY_TONE: Record<PayStatus, BadgeTone> = { unpaid: 'warning', partial: 'info', paid: 'success' }
export function PayStatusBadge({ status, kind }: { status: PayStatus; kind: 'income' | 'expense' }) {
  const { t } = useTranslation()
  return <Badge tone={PAY_TONE[status]}>{t(`finance.payStatus.${kind}.${status}`)}</Badge>
}

const REVIEW_TONE: Record<ReviewStatus | 'mixed', BadgeTone> = { pending: 'neutral', reviewing: 'info', approved: 'success', mixed: 'warning' }
export function ReviewBadge({ status }: { status: ReviewStatus | 'mixed' }) {
  const { t } = useTranslation()
  return <Badge tone={REVIEW_TONE[status]} className="rounded-md">{t(`finance.review.${status}`)}</Badge>
}

const VAT_TONE: Record<VatStatus, BadgeTone> = { eligible: 'success', partial: 'warning', depreciation: 'info', ineligible: 'danger' }
export function VatStatusBadge({ status }: { status: VatStatus }) {
  const { t } = useTranslation()
  return <Badge tone={VAT_TONE[status]}>{t(`finance.vatStatus.${status}`)}</Badge>
}

const EDOC_TONE: Record<EDocStatus, BadgeTone> = { draft: 'neutral', sent: 'info', accepted: 'success', rejected: 'danger', cancelled: 'outline' }
export function EDocStatusBadge({ status }: { status: EDocStatus }) {
  const { t } = useTranslation()
  return <Badge tone={EDOC_TONE[status]}>{t(`finance.edoc.status.${status}`)}</Badge>
}

export function CategoryDot({ color }: { color: number }) {
  return <span className="size-2.5 shrink-0 rounded-full" style={{ background: chartColor(color) }} aria-hidden />
}
